import { useState } from 'react';

import type { PortalStatusState } from '../hooks/usePortalStatus';

interface StatusCardProps {
  state: PortalStatusState;
}

export function StatusCard({ state }: StatusCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <aside
      className={`status-card ${
        isExpanded ? 'status-card-expanded' : ''
      }`}
    >
      <button
        type="button"
        className="status-card-toggle"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <StatusIndicator state={state.status} />

        <span className="status-card-chevron" aria-hidden="true">
          ⌄
        </span>
      </button>

      {isExpanded ? (
        <div className="status-card-content">
          <StatusContent state={state} />
        </div>
      ) : null}
    </aside>
  );
}

function StatusIndicator({
  state,
}: {
  state: PortalStatusState["status"];
}) {
  const labels: Record<
    PortalStatusState["status"],
    string
  > = {
    loading: "Checking",
    online: "Connected",
    authenticationRequired: "Sign-in required",
    forbidden: "Access denied",
    serverError: "Server error",
    offline: "Offline",
  };

  return (
    <span
      className={`status-pill status-pill-${state}`}
    >
      <span className="status-dot" />
      {labels[state]}
    </span>
  );
}

function StatusContent({
  state,
}: StatusCardProps) {
  if (state.status === "loading") {
    return (
      <p className="status-message">
        Connecting to the API…
      </p>
    );
  }

  if (state.status === "authenticationRequired") {
    return (
      <div className="status-panel">
        <strong>Authentication required</strong>
        <span>{state.message}</span>
      </div>
    );
  }

  if (state.status === "forbidden") {
    return (
      <div className="error-panel">
        <strong>Access denied</strong>
        <span>{state.message}</span>
      </div>
    );
  }

  if (state.status === "serverError") {
    return (
      <div className="error-panel">
        <strong>API server error</strong>
        <span>{state.message}</span>
      </div>
    );
  }

  if (state.status === "offline") {
    return (
      <div className="error-panel">
        <strong>API offline</strong>
        <span>{state.message}</span>
      </div>
    );
  }

  return (
    <dl className="status-details">
      <div>
        <dt>API message</dt>
        <dd>{state.data.message}</dd>
      </div>

      <div>
        <dt>Module</dt>
        <dd>{state.data.module.Name}</dd>
      </div>

      <div>
        <dt>Module version</dt>
        <dd>{state.data.module.Version}</dd>
      </div>

      <div>
        <dt>API time</dt>
        <dd>{formatDate(state.data.timeUtc)}</dd>
      </div>
    </dl>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
}